export interface User {
  id: string;
  lineUUID: string;
  contactPersonName: string;
  email: string;
  mobile: string;
  companyName: string;
  province: string;
  lineImage: string;
  lineEmail: string;
  lineName: string;
  lineLanguage: string;
  event: string;
  eventId: string;
  salesPerson: string;
  eventSalesPerson: string;
  customerType: string;
  interested_in: string[];
  image: string;
  createdAt: string;
  lastVisit: string;
  receivePromotion?: boolean;
  verifiedMobile?: boolean;
}

export interface CompanyEvent {
  RECORD_ID: number;
  EVENT_NAME: string;
  DESCRIPTION: string;
  LOCATION: string;
  FROM_DATE: string;
  TO_DATE: string;
  COLOR: string;
  LOGO: string | null;
  STATUS: string;
  MAX_GIFT_COUNT: number;
}

export interface UserGift {
  id: string;
  eventId: string;
  giftNumber: number;
  giftStatus: 'DISTRIBUTED' | 'PENDING' | 'CANCELLED' | 'GENERATED' | 'SCANNED';
  leadId: string;
  distributedAt: string;
  createdAt: string;
  salesPersonId: string;
  leadName: string;
  salesPersonName: string;
  qrCode: string;
}

interface ApiResponse {
  error: boolean;
  data: User | null;
  status: number;
}

interface EventsApiResponse {
  error: boolean;
  message: string;
  data: CompanyEvent[];
}

interface GiftsApiResponse {
  error: boolean;
  data: UserGift[];
  status: number;
}

interface UserEventData {
  leadId: string;
  eventId: string;
  eventName: string;
  contactPersonName: string;
  customerType: string;
  lineUUID: string;
  mobile: string;
  salesPerson: string;
}

interface UserEventResponse {
  error: boolean;
  data: {
    eventName: string;
    eventId: string;
    mobile: string;
    customerType: string;
    userId: string;
    createdAt: string;
    contactPersonName: string;
    salesPerson: string;
    lineUUID: string;
  };
  status: number;
}

import { API_CONFIG, API_ENDPOINTS, validateApiConfig } from './api-config';

// Log configuration status (don't throw error on module load)
validateApiConfig(false);

export const apiService = {
  checkUser: async (lineId: string): Promise<User | null> => {
    try {
      const response = await fetch(API_ENDPOINTS.LEADS.CHECK_USER(lineId));
      const result: ApiResponse = await response.json();
      
      if (result.error || !result.data) {
        return null;
      }
      
      return result.data;
    } catch (error) {
      console.error('Error checking user:', error);
      return null;
    }
  },

  updateUser: async (userId: string, userData: {
    lineUUID: string;
    contactPersonName: string;
    email: string;
    mobile: string;
    companyName: string;
    province: string;
    eventName: string;
    eventId: string;
    interested_in?: string[];
    lineLanguage?: string;
    receivePromotion?: boolean;
    verifiedMobile?: boolean;

  }): Promise<User> => {
    try {
      const response = await fetch(API_ENDPOINTS.LEADS.UPDATE_USER(userId), {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...userData,
          event: userData.eventName || 'Unknown Event',
          eventId: userData.eventId || '0',
          salesPerson: API_CONFIG.DEFAULT_P_USER,
          eventSalesPerson: `${userData.eventId}#${API_CONFIG.DEFAULT_P_USER}`,
          customerType: 'customer',
          interested_in: userData.interested_in || [],
          image: '',
          lineImage: '',
          lineEmail: userData.email,
          lineLanguage: 'en',
          receivePromotion: userData.receivePromotion || false,
          verifiedMobile: userData.verifiedMobile || false
        }),
      });

      const result: ApiResponse = await response.json();
      
      if (result.error || !result.data) {
        throw new Error('Update failed');
      }
      
      return result.data;
    } catch (error) {
      console.error('Error updating user:', error);
      throw error;
    }
  },

  registerUser: async (userData: {
    lineUUID: string;
    contactPersonName: string;
    email: string;
    mobile: string;
    companyName: string;
    province: string;
    eventName: string;
    eventId: string;
    interested_in?: string[];
    lineLanguage?: string;
    receivePromotion?: boolean;
  }): Promise<User> => {
    try {
      const response = await fetch(API_ENDPOINTS.LEADS.CREATE_USER, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...userData,
          event: userData.eventName || 'Unknown Event',
          eventId: userData.eventId || '0',
          salesPerson: API_CONFIG.DEFAULT_P_USER,
          eventSalesPerson: `${userData.eventId}#${API_CONFIG.DEFAULT_P_USER}`,
          customerType: 'customer',
          interested_in: userData.interested_in || [],
          image: '',
          lineImage: '',
          lineEmail: userData.email,
          lineLanguage: 'en',
          receivePromotion: userData.receivePromotion || false,
        }),
      });

      const result: ApiResponse = await response.json();
      
      if (result.error || !result.data) {
        throw new Error('Registration failed');
      }
      
      return result.data;
    } catch (error) {
      console.error('Error registering user:', error);
      throw error;
    }
  },

  getCompanyEvents: async (): Promise<CompanyEvent | null> => {
    try {
      const params = new URLSearchParams({
        company: API_CONFIG.DEFAULT_P_COM || 'JB',
        userCode: API_CONFIG.DEFAULT_P_USER || 'JBT04',
        recordID: '',
        status: 'A',
        haveMaxCount: 'Y'
      });

      const response = await fetch(`${API_ENDPOINTS.JAGOTA.COMPANY_EVENTS}?${params}`);
      const result: EventsApiResponse = await response.json();
      
      if (result.error || !result.data || result.data.length === 0) {
        return null;
      }
      
      // Always return the first event
      return result.data[0];
    } catch (error) {
      console.error('Error fetching company events:', error);
      return null;
    }
  },

  getUserGifts: async (leadId: string, eventId: string): Promise<UserGift[]> => {
    try {
      const response = await fetch(API_ENDPOINTS.LEADS.USER_GIFTS(leadId, eventId));
      const result: GiftsApiResponse = await response.json();
      
      if (result.error || !result.data) {
        return [];
      }
      
      return result.data;
    } catch (error) {
      console.error('Error fetching user gifts:', error);
      return [];
    }
  },

   updateGiftStatus: async (leadId: string, giftStatus: 'DISTRIBUTED' | 'PENDING' | 'CANCELLED' = 'DISTRIBUTED', amountBought?: string): Promise<{ success: boolean; message: string }> => {
    try {
      const response = await fetch(`${API_CONFIG.SERVERLESS_API}/leads/gifts/${leadId}/status`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          giftStatus,
          salesPersonId: API_CONFIG.DEFAULT_P_USER || 'jb',
          salesPersonName: `${API_CONFIG.DEFAULT_P_USER || 'jb'}t4`,
          amountBought: amountBought || '-',
          canHaveMultiple: true // remove me
        }),
      });

      const responseData = await response.json();

      if (response.ok) {
        return {
          success: true,
          message: responseData.message || 'Gift status updated successfully'
        };
      } else {
        return {
          success: false,
          message: responseData.message || 'Failed to update gift status'
        };
      }
    } catch (error) {
      console.error('Error updating gift status:', error);
      return {
        success: false,
        message: 'Network error: Failed to update gift status'
      };
    }
  },

  verifyOTP: async (phoneNumber: string, otp: string): Promise<{ success: boolean; message: string }> => {
    try {
      // Simulate API delay
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      // Fake verification logic - accept any 6-digit OTP or specific demo codes
      if (otp.length === 6 && /^\d{6}$/.test(otp)) {
        return {
          success: true,
          message: 'OTP verified successfully'
        };
      }
      
      return {
        success: false,
        message: 'Invalid OTP code. Please try again.'
      };
    } catch (error) {
      console.error('Error verifying OTP:', error);
      return {
        success: false,
        message: 'Verification failed. Please try again.'
      };
    }
  },

  sendOTP: async (): Promise<{ success: boolean; message: string }> => {
    try {
      // Simulate API delay
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Always return success for demo purposes
      return {
        success: true,
        message: 'OTP sent successfully'
      };
    } catch (error) {
      console.error('Error sending OTP:', error);
      return {
        success: false,
        message: 'Failed to send OTP. Please try again.'
      };
    }
  },

  validateOTP: async (phoneNumber: string, otp: string): Promise<{ success: boolean; message: string }> => {
    try {
      // Simulate API delay
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      // Fake validation logic - accept specific demo codes or any 6-digit OTP
      const validDemoCodes = ['123456', '000000', '111111'];
      
      if (validDemoCodes.includes(otp) || (otp.length === 6 && /^\d{6}$/.test(otp))) {
        return {
          success: true,
          message: 'OTP verified successfully'
        };
      }
      
      // Simulate occasional server errors for testing
      if (otp === '999999') {
        throw new Error('Server error');
      }
      
      return {
        success: false,
        message: 'Invalid OTP code. Please try again.'
      };
    } catch (error) {
      console.error('Error validating OTP:', error);
      return {
        success: false,
        message: 'Verification failed. Please try again.'
      };
    }
  },

  createGift: async (data: {
    eventId: string;
    leadId: string;
    leadName: string;
    giftNumber: number;
    qrCode?: string;
  }): Promise<{ success: boolean; message: string; gift?: UserGift }> => {
    try {
      const response = await fetch(`${API_CONFIG.SERVERLESS_API}/leads/gifts`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          eventId: data.eventId,
          leadId: data.leadId,
          leadName: data.leadName,
          giftNumber: data.giftNumber,
          qrCode: data.qrCode || ''
        }),
      });

      const result = await response.json();

      if (response.ok) {
        return {
          success: true,
          message: 'Gift created successfully',
          gift: result.data
        };
      } else {
        return {
          success: false,
          message: result.message || 'Failed to create gift'
        };
      }
    } catch (error) {
      console.error('Error creating gift:', error);
      return {
        success: false,
        message: 'Network error: Failed to create gift'
      };
    }
  },

  createUserEvent: async (userData: UserEventData): Promise<{ success: boolean; message: string; data?: UserEventResponse['data'] }> => {
    try {
      const response = await fetch(API_ENDPOINTS.LEADS.USER_EVENTS, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(userData),
      });

      const result: UserEventResponse = await response.json();

      if (response.ok && !result.error) {
        return {
          success: true,
          message: 'User event created successfully',
          data: result.data
        };
      } else {
        return {
          success: false,
          message: 'Failed to create user event'
        };
      }
    } catch (error) {
      console.error('Error creating user event:', error);
      return {
        success: false,
        message: 'Network error: Failed to create user event'
      };
    }
  }
};